import { useState } from 'react';

import type { ArticleImage } from 'common/types/Article';

interface Media extends ArticleImage {
  type?: number;
}
interface UseCarouselProps {
  slidesToShow: number; // Can be 1, 1.3, or 2
  items: Media[];
  wrapAround?: boolean;
  initialSlide?: number;
}

export const useCarousel = ({
  items,
  slidesToShow,
  wrapAround = false,
  initialSlide = 0,
}: UseCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialSlide);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const totalItems = items.length;
  const maxIndex = totalItems - 1;
  const isFullSlideMode = Number.isInteger(slidesToShow);

  // determine how many slides to move
  const slidesToMove = isFullSlideMode ? slidesToShow : 1;

  const goToSlide = (index: number) => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    // ensure index is within bounds
    let normalizedIndex = Math.min(Math.max(0, index), maxIndex);

    // if slidesToShow is an integer, ensure the index aligns with slidesToShow
    if (isFullSlideMode) {
      normalizedIndex = Math.floor(normalizedIndex / slidesToShow) * slidesToShow;
    }
    setCurrentIndex(normalizedIndex);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const nextSlide = () => {
    const nextIndex = currentIndex + slidesToMove;
    const isLastSlideVisible = currentIndex >= maxIndex;

    if (isLastSlideVisible) {
      if (wrapAround) {
        goToSlide(0);
      }
    } else if (nextIndex > maxIndex) {
      goToSlide(maxIndex);
    } else {
      goToSlide(nextIndex);
    }
  };

  const previousSlide = () => {
    const prevIndex = currentIndex - slidesToMove;

    if (prevIndex < 0) {
      if (wrapAround) {
        // Go to last slide for both integer and fractional cases
        goToSlide(maxIndex);
      } else {
        goToSlide(0);
      }
    } else {
      goToSlide(prevIndex);
    }
  };

  // For fractional slidesToShow, each image gets its own pagination index
  // For integer slidesToShow, pagination index is based on the group
  const paginationIndex = isFullSlideMode ? Math.floor(currentIndex / slidesToShow) : currentIndex;

  // Calculate total number of pages
  const slideCount = isFullSlideMode ? Math.ceil(items.length / slidesToShow) : items.length;

  const goToPage = (pageIndex: number) => {
    const targetIndex = isFullSlideMode ? pageIndex * slidesToShow : pageIndex;
    goToSlide(targetIndex);
  };

  return {
    cloneImages: isFullSlideMode ? items : [...items, items[0]],
    currentIndex,
    paginationIndex,
    isTransitioning,
    slideCount,
    goToSlide,
    goToPage,
    nextSlide,
    previousSlide,
  };
};
